import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Component, EventEmitter, Input, OnChanges, Output, SimpleChanges } from '@angular/core';
import { catchError, finalize, map, Observable, of, take } from 'rxjs';
import { environment } from '../../../../../environments/environment';
import { FileUploadAnswer } from 'src/app/common/utils';
import { Image } from './interface/image.interface';
import { GenericFileUploadService } from 'src/app/common/services/generic-file-upload.service';
import {
    defaultMaxImageFileSizeBytes,
    ImageUploadValidationResult,
    ImageUploadValidator,
    supportedImageExtensions,
} from './utils/image-upload.validator';

@Component({
    selector: 'app-reactive-upload-images',
    standalone: false,
    templateUrl: './upload-images.component.html',
    styleUrls: ['./upload-images.component.scss']
})
export class ReactiveUploadImagesComponent<T> implements OnChanges {
    @Input() public formModel = {} as T;
    @Input() public formType = 0;
    @Input() public buttonLabel = '';
    @Input() public imageName: string | null = null;
    @Input() public imageFolder = '';
    @Input() public imageDisplay: 'inline' | 'link' | 'header' | string = '';
    @Input() public accept = 'image/*';
    @Input() public allowedExtensions: string[] | null = null;
    @Input() public invalidFormatMessage = '';
    @Input() public uploadUrl: string | null = null;
    @Input() public viewFolder: string | null = null;
    @Input() public showPreview = true;
    @Input() public maxFileSizeBytes: number | null = defaultMaxImageFileSizeBytes;
    @Input() public uploadId: string | number | null = null;

    @Output() public sendImageName: EventEmitter<string> = new EventEmitter<string>();

    public imageModel: Image = { imagen: '', image_after: '' };
    public readonly serverUrl = environment.serverUrl;
    public isUploading = false;
    public uploadError: string | null = null;

    constructor(
        private readonly fileUploadService: GenericFileUploadService,
        private readonly http: HttpClient,
    ) {}

    /**
     * Keeps the preview image synchronized with the current form model.
     */
    public ngOnChanges(changes: SimpleChanges): void {
        if (changes['formModel']?.currentValue) {
            this.imageModel = changes['formModel'].currentValue as Image;
        }
    }

    /**
     * Uploads a file to the server.
     */
    public uploadFile(input: File | Event): void {
        const file = this.extractFile(input);

        this.uploadError = null;

        if (!file) {
            return;
        }

        const validation = this.validateFile(file);
        if (!validation.isValid) {
            this.uploadError = validation.message;
            return;
        }

        this.isUploading = true;

        this.uploadSelectedFile(file)
            .pipe(
                take(1),
                catchError((error: unknown) => {
                    this.uploadError = this.getUploadErrorMessage(error);
                    return of(null);
                }),
                finalize(() => {
                    this.isUploading = false;
                })
            )
            .subscribe((fileName) => {
                if (fileName) {
                    this.patchCurrentImageValue(fileName);
                    this.sendImageName.emit(fileName);
                }
            });
    }

    public hasCurrentImage(): boolean {
        return this.getCurrentImageValue().length > 0;
    }

    /**
     * Opens the uploaded file in a separate window.
     */
    public viewImage(): void {
        const imageValue = this.getCurrentImageValue();

        if (!imageValue) {
            this.uploadError = 'No hay ningún archivo disponible para visualizar.';
            return;
        }

        const imageWindow = window.open('', '_blank', 'width=600,height=600');

        if (!imageWindow) {
            this.uploadError = 'El navegador ha bloqueado la ventana emergente. Permite pop-ups para este sitio.';
            return;
        }

        imageWindow.location.href = this.getFileUrl(imageValue);
        imageWindow.focus();
    }

    public clearUploadError(): void {
        this.uploadError = null;
    }

    public getViewLabel(): string {
        return this.isImageUpload() ? 'Ver foto' : 'Ver documento';
    }// getViewLabel();

    public getInlinePreviewUrl(): string {
        return this.getFileUrl(this.getCurrentImageValue(), false);
    }// getInlinePreviewUrl();

    private uploadSelectedFile(file: File): Observable<string | null> {
        const customUploadUrl = this.uploadUrl?.trim() ?? '';

        if (customUploadUrl.length > 0) {
            return this.uploadToCustomEndpoint(file, customUploadUrl);
        }// if();

        return this.fileUploadService.uploadGeneralImage(file, this.imageFolder);
    }// uploadSelectedFile();

    private uploadToCustomEndpoint(file: File, uploadUrl: string): Observable<string> {
        const formData = new FormData();
        const headers = new HttpHeaders({ Accept: 'application/json' });

        formData.append('uploadFile', file, file.name);
        this.appendUploadIdentifier(formData);

        return this.http.post<FileUploadAnswer>(`${this.serverUrl}${uploadUrl}`, formData, { headers }).pipe(
            map((response) => this.extractStoredFileName(response)),
        );
    }// uploadToCustomEndpoint();

    private extractStoredFileName(response: FileUploadAnswer): string {
        const candidates = [
            response.fileName,
            response.file,
            response.target_file,
            response.aux_target_file,
        ];

        for (const candidate of candidates) {
            const fileName = this.extractFileName(candidate);

            if (fileName.length > 0) {
                return fileName;
            }// if();
        }// for();

        return '';
    }// extractStoredFileName();

    private extractFileName(value: string | undefined): string {
        const rawValue = String(value ?? '').trim();

        if (rawValue.length === 0) {
            return '';
        }// if();

        const normalizedValue = rawValue.replace(/\\/g, '/');
        const slashIndex = normalizedValue.lastIndexOf('/');

        return slashIndex >= 0 ? normalizedValue.slice(slashIndex + 1) : normalizedValue;
    }// extractFileName();

    private patchCurrentImageValue(fileName: string): void {
        if (!this.imageName) {
            return;
        }// if();

        this.imageModel = {
            ...this.imageModel,
            [this.imageName]: fileName,
        };
    }// patchCurrentImageValue();

    private appendUploadIdentifier(formData: FormData): void {
        if (this.uploadId === null || this.uploadId === undefined) {
            return;
        }// if();

        const idValue = String(this.uploadId).trim();

        if (idValue.length > 0) {
            formData.append('id', idValue);
        }// if();
    }// appendUploadIdentifier();

    private getCurrentImageValue(): string {
        if (!this.imageName) {
            return '';
        }

        return String(this.imageModel[this.imageName] ?? '').trim();
    }

    private getFileUrl(fileName: string, preferLegacyInlinePath = true): string {
        const viewFolder = this.viewFolder?.trim() ?? '';

        if (viewFolder.length > 0) {
            return this.joinUrl(this.serverUrl, viewFolder, fileName);
        }// if();

        if (preferLegacyInlinePath) {
            return this.joinUrl(this.serverUrl, 'mto', this.imageFolder, fileName);
        }// if();

        return this.joinUrl(this.serverUrl, this.imageFolder, fileName);
    }// getFileUrl();

    private joinUrl(...parts: string[]): string {
        const normalizedParts = parts
            .filter((part) => part.trim().length > 0)
            .map((part, index) => index === 0 ? part.replace(/\/+$/, '') : part.replace(/^\/+|\/+$/g, ''));

        return normalizedParts.join('/');
    }// joinUrl();

    private validateFile(file: File): ImageUploadValidationResult {
        if (this.allowedExtensions === null) {
            return ImageUploadValidator.validate(file, this.maxFileSizeBytes ?? defaultMaxImageFileSizeBytes);
        }// if();

        const extension = this.getExtension(file.name);
        const allowedExtensions = new Set(this.allowedExtensions.map((allowedExtension) => allowedExtension.toLowerCase()));

        if (!allowedExtensions.has(extension)) {
            return {
                isValid: false,
                message: this.invalidFormatMessage.trim() || 'Formato de archivo no válido.',
            };
        }// if();

        const maxSize = this.maxFileSizeBytes ?? defaultMaxImageFileSizeBytes;
        if (file.size > maxSize) {
            return {
                isValid: false,
                message: `El archivo supera el tamaño máximo permitido (${this.formatBytes(maxSize)}).`,
            };
        }// if();

        return {
            isValid: true,
            message: null,
        };
    }// validateFile();

    public isImageUpload(): boolean {
        const acceptedExtensions = this.allowedExtensions ?? Array.from(supportedImageExtensions);

        return acceptedExtensions.every((extension) => supportedImageExtensions.has(extension.toLowerCase()));
    }// isImageUpload();

    private extractFile(input: File | Event): File | null {
        if (input instanceof File) {
            return input;
        }

        return (input.target as HTMLInputElement | null)?.files?.[0] ?? null;
    }

    private getExtension(fileName: string): string {
        return fileName.split('.').pop()?.toLowerCase() ?? '';
    }// getExtension();

    private formatBytes(bytes: number): string {
        const bytesPerMegabyte = 1024 * 1024;
        const sizeInMb = bytes / bytesPerMegabyte;

        return `${sizeInMb.toFixed(0)} MB`;
    }// formatBytes();

    private getUploadErrorMessage(error: unknown): string {
        if (error instanceof Error && error.message.trim() !== '') {
            return `Error al subir el archivo: ${error.message}`;
        }

        return 'Error al subir el archivo. Vuelve a intentarlo.';
    }
}
